/**
 * DecisionsTab Component
 *
 * Decision log tab for investments - displays history of decisions made
 * by DecisionMakers in descending order by decision date.
 */

import { useDecisions } from '@medsuite/api-client';
import { Card, Spinner, Alert } from '@medsuite/ui-components';
import React from 'react';

import type { Investment, InvestmentDecision, InvestmentDecisionStatus } from '@medsuite/models';

interface DecisionsTabProps {
  investment: Investment;
}

/**
 * Decision status labels
 */
const DECISION_STATUS_LABELS: Record<InvestmentDecisionStatus, string> = {
  N: 'New',
  S: 'Submitted',
  A: 'Approved',
  D: 'Deferred',
  R: 'Rejected',
  P: 'Planned',
  I: 'In Progress',
  O: 'Complete',
  C: 'Cancelled',
};

/**
 * Badge classes for decision statuses with proper contrast
 */
const DECISION_BADGE_CLASSES: Record<InvestmentDecisionStatus, string> = {
  N: 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white',
  S: 'bg-blue-100 text-blue-900 dark:bg-blue-800 dark:text-white',
  A: 'bg-green-100 text-green-900 dark:bg-green-800 dark:text-white',
  D: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-700 dark:text-white',
  R: 'bg-red-100 text-red-900 dark:bg-red-800 dark:text-white',
  P: 'bg-purple-100 text-purple-900 dark:bg-purple-800 dark:text-white',
  I: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-800 dark:text-white',
  O: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-800 dark:text-white',
  C: 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white',
};

/**
 * Format date for display
 */
const formatDate = (date: Date | string) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date without time
 */
const formatDateOnly = (date: Date | string) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get initials from name
 */
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Decision item display component
 */
const DecisionItem: React.FC<{ decision: InvestmentDecision }> = ({ decision }) => {
  const statusLabel = DECISION_STATUS_LABELS[decision.investment_status];
  const badgeClasses = DECISION_BADGE_CLASSES[decision.investment_status];

  return (
    <div className="flex gap-4 py-4">
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-semibold text-sm">
          {getInitials(decision.decision_maker_name)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${badgeClasses}`}
          >
            {statusLabel}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {formatDate(decision.decision_date)}
          </span>
        </div>

        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {decision.decision_maker_name}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({decision.decision_maker_email})
          </span>
        </div>

        {decision.decision_rationale && (
          <div className="mt-2 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {decision.decision_rationale}
          </div>
        )}

        {decision.reconsider_date && (
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium">Reconsider Date:</span>{' '}
            {formatDateOnly(decision.reconsider_date)}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * DecisionsTab Component
 */
export const DecisionsTab: React.FC<DecisionsTabProps> = ({ investment }) => {
  // Fetch decisions
  const { data: decisionsData, isLoading, isError, error } = useDecisions(investment.investment_id);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading decisions...</span>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <Alert variant="error">
        <p className="font-semibold">Failed to load decisions</p>
        <p className="text-sm">{(error as Error)?.message || 'Unknown error'}</p>
      </Alert>
    );
  }

  const decisions = decisionsData?.decisions || [];

  // Sort decisions by date descending (most recent first)
  const sortedDecisions = [...decisions].sort(
    (a, b) => new Date(b.decision_date).getTime() - new Date(a.decision_date).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {decisionsData?.summary && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Decision Summary
            </h3>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Total Decisions
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {decisionsData.summary.total_decisions}
                </dd>
              </div>
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 text-center">
                <dt className="text-sm font-medium text-green-600 dark:text-green-400">Approved</dt>
                <dd className="mt-1 text-2xl font-semibold text-green-700 dark:text-green-300">
                  {decisionsData.summary.approved_count || 0}
                </dd>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4 text-center">
                <dt className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                  Deferred
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-yellow-700 dark:text-yellow-300">
                  {decisionsData.summary.deferred_count || 0}
                </dd>
              </div>
              <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4 text-center">
                <dt className="text-sm font-medium text-red-600 dark:text-red-400">Rejected</dt>
                <dd className="mt-1 text-2xl font-semibold text-red-700 dark:text-red-300">
                  {decisionsData.summary.rejected_count || 0}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
      )}

      {/* Decision Log */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Decision Log ({decisions.length})
          </h3>

          {sortedDecisions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                No decisions yet
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Decisions will appear here when a DecisionMaker approves, defers, rejects, or
                cancels this investment.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedDecisions.map((decision) => (
                <DecisionItem key={decision.investment_decision_id} decision={decision} />
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DecisionsTab;
